import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardBody, Col, Row, Button, Spinner, Badge, Alert } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
// The Active sheet's own table (owner, 2026-09-26): the historic view shows the same
// columns in the same Property Fields order with the same colour coding, read-only.
import { MasterSheetTable } from "../MasterSheetTable";
import { PropertySelector } from "../ArchivedMastersheet/PropertySelector";
import { PreviewTenantForm } from "../ArchivedMastersheet/PreviewTenantForm/PreviewTenantForm";
import {
  getMastersheet,
  getHistoricAsOf,
  exportHistoricAsOfExcel,
  createHistoricSnapshot,
} from "../TenantForm/Helpers/api";

/** Today in Edmonton, the day the sheet's history is kept in. */
const todayIso = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Edmonton" }).format(new Date());

const HistoricMastersheet = () => {
  const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || "{}");
  const userData = parsedAuthUser.userData || parsedAuthUser;
  const isCompany = userData?.role === "company";

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState({ property_name: "", property_id: "", building_name: "" });
  const [sheetMeta, setSheetMeta] = useState(null); // live mastersheet doc (for _id, property_id, building_name)
  const [asOfDate, setAsOfDate] = useState(todayIso());
  const [historicSheet, setHistoricSheet] = useState(null); // { ...sheetMeta, items } for MasterSheetTable
  const [accuracy, setAccuracy] = useState(null); // { source, snapshotUsed, approxCount, orphans, unitCount }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [previewTenant, setPreviewTenant] = useState(null);
  const latestQuery = useRef(0);

  const isValidSelection =
    !!filterData.property_id && (filterData.building_list?.length === 0 || !!filterData.building_name);

  useEffect(() => {
    let active = true;
    if (!isValidSelection) return undefined;
    setError(null);
    setHistoricSheet(null);
    setAccuracy(null);
    setSheetMeta(null);
    getMastersheet({ property_id: filterData.property_id, building_name: filterData.building_name })
      .then(({ data }) => {
        if (active && data?._id) setSheetMeta(data);
      })
      .catch((err) => {
        const message = err?.response?.data?.message || "Failed to load mastersheet.";
        if (active && message !== "Building name is required for this property") setError(message);
      });
    return () => {
      active = false;
    };
  }, [filterData.property_id, filterData.building_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const runQuery = useCallback(async () => {
    if (!sheetMeta?._id || !asOfDate) return;
    const queryId = ++latestQuery.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await getHistoricAsOf(sheetMeta._id, asOfDate);
      if (queryId !== latestQuery.current) return; // a newer date was picked meanwhile
      if (!resp?.success) throw new Error(resp?.message || "Failed");
      const rows = resp.data?.rows || [];
      // `items` are shaped like the Active sheet's (Property Fields order + labels);
      // an older server only sends `rows`.
      const items = resp.data?.items || rows.map((r) => r.item).filter(Boolean);
      setHistoricSheet({ ...sheetMeta, items, mastersheetId: sheetMeta._id });
      setAccuracy({
        source: resp.data?.source,
        snapshotUsed: resp.data?.snapshotUsed,
        approxCount: rows.filter((r) => r.accuracy === "approximate").length,
        orphans: resp.data?.reconstruction_orphans || 0,
        unitCount: items.length,
      });
    } catch (err) {
      if (queryId !== latestQuery.current) return;
      setError(err?.response?.data?.message || err.message || "Query failed.");
      setHistoricSheet(null);
      setAccuracy(null);
    } finally {
      if (queryId === latestQuery.current) setLoading(false);
    }
  }, [sheetMeta, asOfDate]);

  // The sheet for the chosen day loads as soon as a building and a date are set.
  useEffect(() => {
    runQuery();
  }, [runQuery]);

  const handleExport = async () => {
    if (!sheetMeta?._id) return;
    setDownloading(true);
    try {
      const blob = await exportHistoricAsOfExcel(sheetMeta._id, asOfDate);
      saveAs(new Blob([blob]), `Historic_${filterData.building_name || "sheet"}_${asOfDate}.xlsx`);
    } catch (err) {
      Swal.fire("Error", "Excel export failed.", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleSnapshotNow = async () => {
    if (!sheetMeta?._id) return;
    const confirm = await Swal.fire({
      title: "Take snapshot now?",
      text: `Freeze the current state of ${filterData.building_name || "this mastersheet"} as an exact historical record.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Snapshot now",
    });
    if (!confirm.isConfirmed) return;
    setSnapshotting(true);
    try {
      const resp = await createHistoricSnapshot(sheetMeta._id);
      if (!resp?.success) throw new Error(resp?.message || "Snapshot failed");
      Swal.fire("Done", "Snapshot saved.", "success");
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err.message, "error");
    } finally {
      setSnapshotting(false);
    }
  };

  // Rows are a past day's: the tenant form opens as a preview only.
  const handleEditTenant = (tenantData) => {
    setPreviewTenant(tenantData);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setPreviewTenant(null);
  };

  const shownDate = asOfDate
    ? new Date(`${asOfDate}T12:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })
    : "";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <PreviewTenantForm
          show={showForm}
          handleClose={handleCloseForm}
          propertyId={filterData.property_id || ""}
          propertyName={filterData.property_name}
          buildingName={filterData.building_name}
          previewData={previewTenant}
          tenantInfo={previewTenant}
        />
        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center mb-2">
                  <h4 className="mb-0">Historic Mastersheet</h4>
                  {isCompany && (
                    <Button
                      size="sm"
                      color="dark"
                      outline
                      className="ms-auto"
                      onClick={handleSnapshotNow}
                      disabled={!sheetMeta?._id || snapshotting}
                    >
                      {snapshotting ? <Spinner size="sm" /> : "Snapshot now"}
                    </Button>
                  )}
                </div>
                <PropertySelector
                  isFilterOpen={isFilterOpen}
                  setIsFilterOpen={setIsFilterOpen}
                  userData={userData}
                  filterData={filterData}
                  setFilterData={setFilterData}
                />
              </CardHeader>

              <CardBody>
                {!isValidSelection && (
                  <Alert color="danger" className="text-center">
                    <strong>⚠️ Please select both a Property and its corresponding Building to proceed.</strong>
                  </Alert>
                )}

                {isValidSelection && sheetMeta && (
                  <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
                    <div>
                      <label className="form-label mb-0 small" htmlFor="historic-date">
                        Show the sheet as it was on
                      </label>
                      <input
                        id="historic-date"
                        type="date"
                        className="form-control"
                        value={asOfDate}
                        max={todayIso()}
                        onChange={(e) => e.target.value && setAsOfDate(e.target.value)}
                      />
                    </div>
                    {loading && <Spinner size="sm" className="mb-2" />}
                    <Button
                      color="success"
                      outline
                      onClick={handleExport}
                      disabled={downloading || !historicSheet}
                      className="ms-auto"
                    >
                      {downloading ? <Spinner size="sm" /> : "Download Excel"}
                    </Button>
                  </div>
                )}

                {error && <Alert color="danger">{error}</Alert>}

                {accuracy && (
                  <Alert color={accuracy.source === "snapshot" ? "success" : "warning"} className="py-2 mb-0">
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                      <strong>{shownDate}</strong>
                      <span className="small">
                        {accuracy.source === "snapshot"
                          ? "Exact: saved copy of the sheet, plus the recorded changes after it"
                          : "Reconstructed from the change history (before copies were kept)"}
                      </span>
                      {accuracy.snapshotUsed && (
                        <span className="small">
                          Copy from {new Date(accuracy.snapshotUsed).toLocaleDateString("en-CA")}
                        </span>
                      )}
                      <span className="small">{accuracy.unitCount} units · read-only</span>
                      {accuracy.approxCount > 0 && (
                        <Badge color="warning" className="text-dark">
                          {accuracy.approxCount} approximate unit{accuracy.approxCount > 1 ? "s" : ""}
                        </Badge>
                      )}
                      {accuracy.orphans > 0 && (
                        <Badge color="warning" className="text-dark">
                          {accuracy.orphans} unit{accuracy.orphans > 1 ? "s" : ""} unrecoverable (deleted without archive)
                        </Badge>
                      )}
                    </div>
                  </Alert>
                )}
              </CardBody>

              {historicSheet && historicSheet.items.length === 0 && (
                <CardBody className="pt-0">
                  <p className="text-muted text-center mb-0">No records exist for this date.</p>
                </CardBody>
              )}

              {historicSheet && historicSheet.items.length > 0 && (
                <MasterSheetTable
                  mastersheet={historicSheet}
                  onEditTenant={handleEditTenant}
                  propertyId={filterData.property_id}
                  buildingName={filterData.building_name}
                  onDataUpdate={runQuery}
                  readOnly
                  referenceDate={asOfDate}
                />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export { HistoricMastersheet };
